import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Phone,
  Mail,
  FileText,
  User,
  Check,
  CheckCircle2,
  AlertTriangle,
} from "lucide-react";
import api from "../../../api";
import { motion, AnimatePresence } from "framer-motion";
import { useNotification } from "../../../context/NotificationContext";

const iconByType = {
  Appel: <Phone className="w-5 h-5 text-secondary" />,
  Email: <Mail className="w-5 h-5 text-warning-500" />,
  "Rendez-vous": <Calendar className="w-5 h-5 text-success-500" />,
  "Message LinkedIn": <Mail className="w-5 h-5 text-primary-500" />,
  "Note interne": <FileText className="w-5 h-5 text-subtle" />,
  Autre: <User className="w-5 h-5 text-subtle" />,
};

const suggestionsParStatut = {
  NEW: [
    { type: "Appel", note: "Premier échange téléphonique" },
    { type: "Email", note: "Envoyer brochure de présentation" },
    { type: "Message LinkedIn", note: "Ajouter sur LinkedIn" },
    { type: "Rendez-vous", note: "Proposer un rendez-vous" },
    { type: "Note interne", note: "Préparer la Lettre d’Entrée en Relation" },
    { type: "Email", note: "Faire signer la Lettre d’Entrée en Relation" },
  ],
  QUALIFIED: [
    { type: "Email", note: "Envoyer le questionnaire patrimonial" },
    { type: "Note interne", note: "Collecter documents financiers et administratifs" },
    { type: "Rendez-vous", note: "Fixer un rendez-vous pour l'audit patrimonial" },
  ],
  CONVERTED: [
    { type: "Note interne", note: "Lancer l’analyse du patrimoine" },
    { type: "Email", note: "Envoyer proposition d’allocation" },
    { type: "Appel", note: "Relancer pour retour sur proposition" },
  ],
  DISQUALIFIED: [
    { type: "Note interne", note: "Clore le dossier (sans suite)" },
  ],
};

export default function ProspectStatus({ prospect, onStatutUpdate }) {
  const [prochainesActions, setProchainesActions] = useState([]);
  const [showAuditButton, setShowAuditButton] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [showFinalModal, setShowFinalModal] = useState(false);
  const { showToast } = useNotification();
  const navigate = useNavigate();
  useEffect(() => {
    fetchNextSteps();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prospect]);

  const fetchNextSteps = async () => {
    if (!(prospect?.id || prospect?._id)) return;
    try {
      const res = await api.get(`/interactions/${(prospect?.id || prospect?._id)}`);
      
      const allInteractions = res.data;
      
      const suggestionsDéjàRéalisées = new Set(
        allInteractions.map((i) => `${i.typeInteraction}_${i.contenu?.trim()}`)
      );

      const futures = allInteractions
        .filter((i) => i.prochaineEtape || i.dateProchaineInteraction)
        .filter((i) => {
          if (!i.prochaineEtape) return true;
          const clé = `${i.typeInteraction}_${i.prochaineEtape.trim()}`;
          return !suggestionsDéjàRéalisées.has(clé);
        })
        .sort((a, b) => new Date(a.dateProchaineInteraction) - new Date(b.dateProchaineInteraction));
      
      const suggestionsBrutes = suggestionsParStatut[prospect?.status || prospect?.statut || "NEW"] || [];
      const suggestionsFiltrées = suggestionsBrutes.filter((sugg) => {
        const clé = `${sugg.type}_${sugg.note.trim()}`;
        return !suggestionsDéjàRéalisées.has(clé);
      });
      
      const actionsFutures = futures.map((item) => ({
        icon: iconByType[item.typeInteraction],
        titre: item.prochaineEtape || "Étape à préciser",
        date: item.dateProchaineInteraction
          ? new Date(item.dateProchaineInteraction).toLocaleDateString()
          : null,
        canal: item.canal,
        type: item.typeInteraction,
      }));
      
      const actionsSuggestions = suggestionsFiltrées.map((sugg) => ({
        icon: iconByType[sugg.type],
        titre: sugg.note,
        type: sugg.type,
        canal: "N/A",
        date: null,
      }));
      
      setProchainesActions([...actionsFutures, ...actionsSuggestions]);

      const rdvAuditEstFixe = allInteractions.some(
        (i) =>
          i.typeInteraction === "Rendez-vous" &&
          i.contenu === "Fixer un rendez-vous pour l'audit patrimonial"
      );
      setShowAuditButton(rdvAuditEstFixe);
    } catch (err) {
      console.error("Erreur récupération prochaines étapes :", err);
      setProchainesActions([]);
    }
  }; 

  const handleMarkAsDone = async (action) => {
    try {
      await api.post("/interactions", {
        typeInteraction: action.type,
        canal: action.canal === "N/A" ? "Autre" : action.canal,
        date: new Date(),
        contenu: action.titre,
        statut: prospect.status || prospect.statut,
        referent: prospect.ownerMembershipId || prospect.referent || "Non assigné",
        prospectId: (prospect?.id || prospect?._id),
        commentaireInterne: "Interaction ajoutée depuis la section 'À Faire'.",
      });
      
      if (
        (prospect.status === "NEW" || prospect.statut === "Prospect") &&
        action.titre === "Faire signer la Lettre d’Entrée en Relation"
      ) {
        await api.put(`/prospects/${(prospect?.id || prospect?._id)}`, { status: "QUALIFIED" });
        
        if (onStatutUpdate) onStatutUpdate("QUALIFIED");
        
        await fetchNextSteps();
        showToast("Interaction enregistrée et statut mis à jour vers 'Prospect Qualifié' !");
        return;
      }
      
      if (
        (prospect.status === "QUALIFIED" || prospect.statut === "Prospect Qualifié") &&
        action.titre === "Fixer un rendez-vous pour l'audit patrimonial"
      ) {
        setShowAuditButton(true);
      }
      
      setProchainesActions(prev => prev.filter(a => a.titre !== action.titre));
      await fetchNextSteps();
      showToast("Interaction enregistrée !");
    } catch (err) {
      console.error("Erreur enregistrement interaction ou mise à jour statut :", err);
    }
  }; 

  const handleConfirmAudit = async () => {
    try {
      const res = await api.post(`/prospects/transformer/${(prospect?.id || prospect?._id)}`, {});
      
      showToast(res.data.message || "Prospect transformé avec succès", "success");
      setShowConfirmModal(false);
      setShowFinalModal(true);
      
    } catch (error) {
      console.error("Erreur lors de la transformation en client :", error);
      showToast("Une erreur est survenue lors de la transformation.", "error");
    }
  }; 

  return (
    <div className="workspace-card bg-surface shadow-hp-sm rounded-xl p-6 h-[300px] flex-1 min-w-[250px] flex flex-col relative overflow-hidden">
      <div className="flex flex-col h-full">
        <h2 className="text-sm font-bold uppercase tracking-wider text-muted mb-4 border-b border-workspace pb-2">À Faire</h2>

        {prospect ? (
          prochainesActions.length > 0 ? (
            <div className="flex-1 pr-2">
              <motion.ul 
                initial="hidden"
                animate="show"
                variants={{
                  hidden: { opacity: 0 },
                  show: { opacity: 1, transition: { staggerChildren: 0.1 } }
                }}
                className="space-y-3"
              >
                {prochainesActions.slice(0, 2).map((action, index) => (
                  <motion.li
                    variants={{
                      hidden: { opacity: 0, x: 20 },
                      show: { opacity: 1, x: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
                    }}
                    key={action.titre}
                    className="group flex flex-col gap-2 p-2 bg-workspace rounded-lg border border-transparent hover:border-workspace hover:shadow-sm transition-all"
                  >
                    <div className="flex items-start gap-3">
                      <div className="p-2 bg-surface rounded-md shadow-sm border border-workspace">
                        {action.icon}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-primary text-sm truncate">
                          {action.titre}
                        </p>
                        <p className="text-xs text-muted mt-0.5 truncate flex items-center gap-1">
                          <span className="font-medium text-subtle">{action.type}</span>
                          {action.canal !== "N/A" && <span>• {action.canal}</span>}
                          {action.date && <span className="text-secondary-600 font-medium">• {action.date}</span>}
                        </p>
                      </div>
                    </div>
                    <button
                      className="flex items-center justify-center gap-1.5 w-full py-1.5 text-xs font-bold text-success-600 bg-success-50 rounded-md border border-success-100 hover:bg-success-100 transition-colors opacity-0 group-hover:opacity-100"
                      onClick={() => handleMarkAsDone(action)}
                    >
                      <Check className="w-3.5 h-3.5" />
                      Marquer comme faite
                    </button>
                  </motion.li>
                ))}
              </motion.ul>
              
              {showAuditButton && (
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mt-4 pt-4 border-t border-workspace flex justify-center"
                >
                  <button
                    className="btn btn-primary w-full bg-hp-primary-600 hover:bg-hp-primary-700"
                    onClick={() => setShowConfirmModal(true)}
                  >
                    Lancer l'Audit Patrimonial
                  </button>
                </motion.div>
              )} 
            </div> 
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-subtle">
              <CheckCircle2 className="w-12 h-12 mb-4 text-success-500" />
              <p className="text-sm font-medium mb-4 text-center">Toutes les actions sont terminées</p>
              <button
                className="btn btn-primary bg-hp-primary-600 hover:bg-hp-primary-700"
                onClick={() => setShowConfirmModal(true)}
              >
                Lancer l'Audit Patrimonial
              </button>
            </div>
          )
        ) : (
          <div className="flex flex-col items-center justify-center flex-1 text-center opacity-60">
            <Calendar className="w-10 h-10 mb-2 text-subtle" />
            <p className="text-sm font-medium text-subtle">
              Sélectionnez un prospect
            </p>
          </div>
        )}
      </div>

      <AnimatePresence>
        {showConfirmModal && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[rgb(var(--hp-ink-950-rgb)/0.5)] backdrop-blur-sm flex justify-center items-center z-[70] p-4"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="workspace-card-elevated p-6 rounded-xl w-full max-w-md"
            >
              <h3 className="text-xl font-bold mb-2 text-primary flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-warning-500" />
                Confirmer la transformation
              </h3>
              <p className="text-sm text-subtle mb-6">
                Cette action transférera les données dans les familles, mettra à jour le statut en "Client" et supprimera ce prospect.
              </p>
              <div className="flex justify-end gap-3 pt-4 border-t border-workspace">
                <button
                  className="btn btn-secondary"
                  onClick={() => setShowConfirmModal(false)}
                >
                  Annuler
                </button>
                <button
                  className="btn btn-primary bg-success-600 border-success-600 hover:bg-success-700"
                  onClick={handleConfirmAudit}
                >
                  <CheckCircle2 className="w-4 h-4 mr-2" /> Confirmer
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showFinalModal && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-[rgb(var(--hp-ink-950-rgb)/0.5)] backdrop-blur-sm flex justify-center items-center z-[70] p-4"
          >
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="workspace-card-elevated p-8 rounded-xl w-full max-w-sm text-center flex flex-col items-center"
            >
              <div className="w-16 h-16 bg-success-50 rounded-full flex items-center justify-center mb-4">
                <CheckCircle2 className="w-8 h-8 text-success-600" />
              </div>
              <h3 className="text-xl font-bold mb-2 text-primary">
                Transformation réussie
              </h3>
              <p className="text-sm text-subtle mb-8">
                Le prospect a été converti en client. Vous pouvez désormais consulter sa fiche famille.
              </p>
              <button
                className="btn btn-primary w-full justify-center"
                onClick={() => navigate("/prospects/familles")}
              >
                Aller à la fiche famille
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
